import React, { useEffect, useContext, useState, useCallback, useMemo } from 'react';
import { CircularProgress, Grid } from '@material-ui/core';
import clsx from 'clsx';
import { modeUIContext, setIsEditingForm, showSuccessNotification } from '@moderepo/mode-ui-state';
import { getAgentFeaturePermissions, ProjectType, RitzAppSettings } from '@moderepo/mode-internal-apis';
import { SelectInputOption, modeSupportedThemes, ModeThemeType, getLanguageByCode, Language, BaseProjectInfoUpdatableInputs,
    capitalizeAll, getThemeName, GenericInfoComp, BaseProjectInfoProps, FieldInputType } from '@moderepo/mode-app-components';
import { fetchCSAppSettings, ritzDataContext, selectAppSettings, selectLoggedInAgent, selectProjectAgent, selectProjectById, updateProjectSettings }
    from '@moderepo/mode-data-state-ritz';
import { getProjectTypeName } from '@moderepo/mode-app-components-internal';
import { useRitzTranslation } from '../../utils/customHooks';
import * as Constants from '../../utils/constants';



export interface ProjectSettingsContainerProps {
    readonly projectId: number;
}

export const ProjectSettingsContainer: React.FC<ProjectSettingsContainerProps> = (props: ProjectSettingsContainerProps) => {
    const { projectId } = props;

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const project = selectProjectById(dataState, projectId);
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const appSettings = selectAppSettings(dataState) as RitzAppSettings;
    const permissions = getAgentFeaturePermissions(projectAgent);
    const { trans } = useRitzTranslation(project);


    const [isEditing, setIsEditing] = useState<boolean>(false);

    const projectType = project && project.supportToolSettings && project.supportToolSettings.projectType
        ? project.supportToolSettings.projectType : ProjectType.DEFAULT;
    const projectLanguage = project && project.supportToolSettings && project.supportToolSettings.locale
        ? getLanguageByCode(Constants.SUPPORTED_LANGUAGES, project.supportToolSettings.locale) : undefined;
    const projectTheme = project && project.supportToolSettings && project.supportToolSettings.theme
        ? project.supportToolSettings.theme : undefined;



    useEffect(() => {
        if (!appSettings) {
            dataDispatch(fetchCSAppSettings()).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [appSettings, dataDispatch]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
    }, [closeEditing]);


    /**
     * On project settings input change
     */
    const onProjectInfoFormInputChange = useCallback(() => {
        // do nothing
    }, []);


    /**
     * Handler for when the user click Save input from the Project Settings
     */
    const onProjectInfoFormSubmit = useCallback(async (inputs: BaseProjectInfoUpdatableInputs): Promise<void> => {

        if (!project || !loggedInAgent) {
            return Promise.resolve();
        }

        const languageInput = inputs.language;
        const themeInput = inputs.theme;

        // If languageInput is provided and it is NOT the same as the current locale or if the theme is provided and not the same as the current
        // theme then make API call to update the project settings
        if (
            (languageInput !== undefined && languageInput !== project.supportToolSettings.locale)       // if locale changed
            || (themeInput !== undefined && themeInput !== project.supportToolSettings.theme)           // or theme changed
        ) {
            // One or both settings changed so we need to make API call to update the settings
            try {

                // Only send the value for the settings that changed.
                await dataDispatch(updateProjectSettings(project.id, loggedInAgent.id, {
                    locale: languageInput !== project.supportToolSettings.locale ? languageInput : undefined,
                    theme : themeInput !== project.supportToolSettings.theme ? themeInput : undefined,
                }));
                uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.project' }));

            } catch (error) {
                return Promise.resolve();
            }
        } else {
            // setting is the same
        }

        closeEditing();
        return Promise.resolve();

    }, [dataDispatch, uiDispatch, closeEditing, project, loggedInAgent]);


    // Create the settings for the fields and cache it
    const fieldsSettings = useMemo((): BaseProjectInfoProps['fields'] | undefined => {
        if (project && appSettings) {
            return {
                id: {
                    label       : capitalizeAll(trans('label.id')),
                    displayValue: project.id.toString(),
                    value       : project.id,
                },
                name: {
                    label       : capitalizeAll(trans('label.name')),
                    displayValue: project.name ? project.name : capitalizeAll(trans('label.none')),
                    value       : project.name,
                    editable    : false,
                },
                description: {
                    label       : capitalizeAll(trans('label.description')),
                    displayValue: project.description ? project.description : capitalizeAll(trans('label.none')),
                    value       : project.description,
                    editable    : false,
                },
                type: {
                    label       : capitalizeAll(trans('label.project_type')),
                    displayValue: projectType
                        ? capitalizeAll(trans(getProjectTypeName(projectType)))
                        : capitalizeAll(trans('label.none')),
                    value   : projectType,
                    editable: false,
                },
                language: {
                    label       : capitalizeAll(trans('label.language')),
                    displayValue: projectLanguage
                        ? capitalizeAll(trans(projectLanguage.name))
                        : capitalizeAll(trans('label.none')),
                    value      : projectLanguage ? projectLanguage.locale : undefined,
                    controlType: FieldInputType.SELECT,
                    options    : [{
                        label: capitalizeAll(trans('label.none')),
                        value: '',
                    } as SelectInputOption<string>,
                    ...Constants.SUPPORTED_LANGUAGES.map((language: Language): SelectInputOption<string> => {
                        return {
                            label: capitalizeAll(trans(language.name)),
                            value: language.locale,
                        };
                    })],
                    editable: permissions.canUpdateProject,
                },
                theme: {
                    label       : capitalizeAll(trans('label.theme')),
                    displayValue: projectTheme
                        ? capitalizeAll(trans(getThemeName(projectTheme)))
                        : capitalizeAll(trans('theme.default')),
                    value      : projectTheme || undefined,
                    controlType: FieldInputType.SELECT,
                    options    : [{
                        label: capitalizeAll(trans(getThemeName(ModeThemeType.DEFAULT))),
                        value: modeSupportedThemes[ModeThemeType.DEFAULT].type,
                    } as SelectInputOption<string>,
                    ...appSettings.supportedThemes.map((type: string): SelectInputOption<string> => {
                        return {
                            label: capitalizeAll(trans(getThemeName(type))),
                            value: modeSupportedThemes[type].type,
                        };
                    })],
                    editable: permissions.canUpdateProject,
                },
            };
        }

        return undefined;
    }, [project, appSettings, permissions.canUpdateProject, projectLanguage, projectTheme, projectType, trans]);


    // Create the settings for the action and cache it
    const actionsSettings = useMemo((): BaseProjectInfoProps['actions'] | undefined => {
        if (permissions.canUpdateProject) {
            return {
                startEdit: {
                    onClick: () => {
                        onChangeEditingMode(true);
                    },
                },
                cancelEdit: {
                    label  : capitalizeAll(trans('label.cancel')),
                    onClick: onCancelEdit,
                },
                saveUpdate: {
                    label  : capitalizeAll(trans('label.save')),
                    onClick: onProjectInfoFormSubmit,
                },
            };
        }

        return undefined;
    }, [permissions.canUpdateProject, onCancelEdit, onChangeEditingMode, onProjectInfoFormSubmit, trans]);


    return (
        <>
            {project && appSettings && fieldsSettings ? (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <GenericInfoComp<BaseProjectInfoProps>
                            className={clsx(isEditing && 'editing')}
                            isEditing={isEditing}
                            title={capitalizeAll(trans('label.project_settings'))}
                            icon="settings"
                            fields={fieldsSettings}
                            actions={actionsSettings}
                            onFieldClicked={() => {
                                onChangeEditingMode(true);
                            }}
                            onFormInputChange={onProjectInfoFormInputChange}
                        />
                    </Grid>
                </Grid>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};
